import React from "react";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { TrialStatusCount } from "@/hooks/useTrialStatusCounts";

export interface TrialStatsFiltersState {
  startDate?: Date;
  endDate?: Date;
  status?: string;
}

interface TrialStatsFiltersProps {
  filters: TrialStatsFiltersState;
  onFiltersChange: (filters: TrialStatsFiltersState) => void;
  statusCounts: TrialStatusCount[];
  totalCount: number;
  isLoading?: boolean;
}

export function TrialStatsFilters({
  filters,
  onFiltersChange,
  statusCounts,
  totalCount,
  isLoading,
}: TrialStatsFiltersProps) {
  const hasActiveFilters = filters.startDate || filters.endDate || filters.status;

  const clearFilters = () => {
    onFiltersChange({});
  };

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      {/* Start Date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[160px] justify-start text-left font-normal",
              !filters.startDate && "text-muted-foreground"
            )}
            disabled={isLoading}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {filters.startDate ? (
              format(filters.startDate, "d MMM yyyy", { locale: th })
            ) : (
              <span>ตั้งแต่วันที่</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={filters.startDate}
            onSelect={(date) => onFiltersChange({ ...filters, startDate: date })}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>

      {/* End Date */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-[160px] justify-start text-left font-normal",
              !filters.endDate && "text-muted-foreground"
            )}
            disabled={isLoading}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {filters.endDate ? (
              format(filters.endDate, "d MMM yyyy", { locale: th })
            ) : (
              <span>ถึงวันที่</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={filters.endDate}
            onSelect={(date) => onFiltersChange({ ...filters, endDate: date })}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>

      {/* Status Filter */}
      <Select
        value={filters.status || "all"}
        onValueChange={(value) => 
          onFiltersChange({ ...filters, status: value === "all" ? undefined : value })
        }
        disabled={isLoading}
      >
        <SelectTrigger className="w-[280px]">
          <SelectValue placeholder="สถานะทั้งหมด" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            ทั้งหมด ({totalCount})
          </SelectItem>
          {statusCounts.map((item) => (
            <SelectItem key={item.status} value={item.status}>
              {item.status} ({item.count})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
          className="h-9 px-3"
        >
          <X className="h-4 w-4 mr-1" />
          ล้างตัวกรอง
        </Button>
      )}
    </div>
  );
}
